import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import { useTheme, listThemes } from '@evi/skins';
import type { ThemeFamilyId } from '@evi/skins';
import {
  SectionLabel,
  ModeSegment,
  ThemeRow,
  PaletteIcon,
  ChevronRightIcon,
} from './theme-picker/primitives.js';
import { driverThemeName } from '../themes/driverDisplayNames.js';

/**
 * Driver PWA picker. Shares row chrome with the admin `ThemePicker`
 * (primitives in `./theme-picker/primitives.tsx`) but swaps the full
 * theme list for a 4-row recents short-list plus a "View all your
 * themes" link to the full Themes page.
 *
 * The picker is logic-free about WHICH themes exist — callers inject:
 *
 *   - `entries`: the unified list of all available themes (admin
 *     families + skin unlocks), already resolved to what the driver
 *     sees: name, description, swatches, active flag, locked flag.
 *   - `recentCodes`: theme codes the driver has most-recently-applied.
 *     Capped upstream (MAX_RECENT_THEMES = 4). May be empty for new
 *     users — the picker falls back to the first 4 entries in order.
 *   - `onApply(code)`: fires when the driver taps an UNLOCKED row.
 *     Callers handle the network call + recency write + React-Query
 *     invalidation + toast.
 *   - `onOpenLocked(code)`: fires when the driver taps a LOCKED row.
 *     Callers navigate to the full Themes page with a focus hint.
 *   - `onOpenAll()`: fires when the driver taps "View all your themes."
 *
 * The mode toggle stays the ThemeProvider's direct setter — mode
 * persistence lives in localStorage, no API call on mode flip.
 */

export interface DriverPickerEntry {
  code: string;
  name: string;
  description?: string;
  swatches: readonly [string, string, string];
  /** True when this is the currently-applied theme. */
  active: boolean;
  /** True when the driver doesn't own this theme (only relevant for
   *  skins; admin families are never locked). Locked rows navigate
   *  to the full Themes page on tap rather than apply. */
  locked: boolean;
  /** 'family' | 'skin' — drives which apply action the caller takes.
   *  The picker itself doesn't branch on this; it's purely a hint
   *  for the `onApply` handler. */
  kind: 'family' | 'skin';
}

export interface DriverThemePickerProps {
  className?: string;
  entries: DriverPickerEntry[];
  recentCodes: string[];
  /** Called when the driver taps an unlocked row. */
  onApply: (entry: DriverPickerEntry) => void;
  /** Called when the driver taps a locked row. */
  onOpenLocked: (entry: DriverPickerEntry) => void;
  /** Called when the driver taps "View all your themes." */
  onOpenAll: () => void;
}

const MAX_INLINE_ROWS = 4;

// Default short-list used when `recentCodes` is empty. Ordered per
// ticket: EVI Default, Luce, Midnight, Sunset (first 4 in registry
// order when both families and seed skins are merged).
const DEFAULT_SHORTLIST: readonly string[] = [
  'default',
  'luce',
  'skin_midnight',
  'skin_sunset',
];

/**
 * Resolve which entries to show inline. Always fills up to
 * MAX_INLINE_ROWS so the driver can always switch to another theme
 * right from the picker — even right after applying their first
 * one (when recents would otherwise contain a single code).
 *
 * Fill order:
 *   1. Driver's own recents (deduped, resolved against entries —
 *      retired codes silently dropped).
 *   2. Pad from DEFAULT_SHORTLIST (anything not already present).
 *   3. Pad from the remaining catalogue in entry order.
 *
 * The result is truncated to MAX_INLINE_ROWS. Locked themes can
 * appear in the padded slots — tapping a locked row routes to the
 * full Themes page rather than applying, which matches the "locked
 * rows never apply silently" rule.
 */
function resolveShortlist(
  recentCodes: string[],
  entries: DriverPickerEntry[],
): DriverPickerEntry[] {
  const byCode = new Map(entries.map((e) => [e.code, e]));
  const out: DriverPickerEntry[] = [];
  const seen = new Set<string>();

  const pushIfResolvable = (code: string): void => {
    if (seen.has(code)) return;
    const entry = byCode.get(code);
    if (!entry) return; // retired / renamed — drop silently
    out.push(entry);
    seen.add(code);
  };

  // Pass 1 — recents, most-recent first.
  for (const code of recentCodes) {
    if (out.length >= MAX_INLINE_ROWS) break;
    pushIfResolvable(code);
  }
  // Pass 2 — default shortlist fill (families + seed skins).
  for (const code of DEFAULT_SHORTLIST) {
    if (out.length >= MAX_INLINE_ROWS) break;
    pushIfResolvable(code);
  }
  // Pass 3 — remaining catalogue in order. Handles the case where
  // the registry ever grows past the default shortlist or where the
  // tenant has added their own skins beyond the 4 starters.
  for (const entry of entries) {
    if (out.length >= MAX_INLINE_ROWS) break;
    pushIfResolvable(entry.code);
  }

  return out;
}

export function DriverThemePicker({
  className,
  entries,
  recentCodes,
  onApply,
  onOpenLocked,
  onOpenAll,
}: DriverThemePickerProps): JSX.Element {
  const { theme, mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // The popover is portal-rendered to document.body and positioned
  // against the trigger, mirroring the top bar's NotificationBell
  // (apps/web/src/components/NotificationBell.tsx). An `absolute`
  // popover anchored inside this right-edge trigger wrapper cannot
  // hold a left gutter on narrow phones — its containing block is the
  // wrapper, not the viewport, and a sticky/transformed top-bar
  // ancestor can capture it anyway. position:fixed off a body portal
  // resolves against the viewport regardless of ancestor layout.
  //   - Wide (>=500px): right-anchored under the trigger at the
  //     popover's natural 300px width; `right` = distance from the
  //     viewport's right edge to the trigger's right edge (8px floor).
  //   - Narrow (<500px): stretched between both viewport edges with
  //     16px gutters (left:16; right:16; width:auto) — matches the
  //     bell's narrow gutter so the two popovers read consistently and
  //     neither ever touches an edge.
  type Anchor =
    | { mode: 'wide'; top: number; right: number }
    | { mode: 'narrow'; top: number };
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const inline = useMemo(() => resolveShortlist(recentCodes, entries), [recentCodes, entries]);

  // Anchor compute + dismiss handlers, mounted/unmounted with `open`
  // (same shape as NotificationBell). Recomputed on resize; dismissed
  // on scroll since the viewport-anchored popover doesn't chase the
  // (sticky) top bar as the page scrolls under it.
  useEffect(() => {
    if (!open) {
      setAnchor(null);
      return;
    }
    const computeAnchor = (): void => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.bottom + 8;
      if (window.innerWidth < 500) {
        setAnchor({ mode: 'narrow', top });
      } else {
        setAnchor({ mode: 'wide', top, right: Math.max(8, window.innerWidth - rect.right) });
      }
    };
    computeAnchor();
    // Outside-click checks BOTH the trigger and the portaled panel —
    // the panel is no longer a DOM descendant of the trigger wrapper,
    // so a single rootRef.contains() check would treat clicks inside
    // the popover as outside and close it on every interaction.
    function onDown(e: Event): void {
      const target = e.target as Node;
      const inButton = buttonRef.current?.contains(target);
      const inPanel = panelRef.current?.contains(target);
      if (!inButton && !inPanel) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScroll(): void {
      setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', computeAnchor);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', computeAnchor);
      window.removeEventListener('scroll', onScroll);
    };
  }, [open]);

  const handleSelect = (entry: DriverPickerEntry): void => {
    setOpen(false);
    if (entry.locked) {
      onOpenLocked(entry);
      return;
    }
    onApply(entry);
  };

  const handleOpenAll = (): void => {
    setOpen(false);
    onOpenAll();
  };

  return (
    <div className={className} style={{ display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Themes"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: open ? 'var(--evi-surface-2)' : 'var(--evi-surface)',
          border: `1px solid ${open ? 'var(--evi-accent)' : 'var(--evi-border)'}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: 0,
          color: 'var(--evi-text)',
          transition: 'background 0.2s ease, border-color 0.2s ease',
        }}
      >
        <PaletteIcon />
      </button>

      {open && anchor && createPortal(
        <div
          ref={panelRef}
          role="menu"
          aria-label="Themes"
          style={{
            position: 'fixed',
            ...(anchor.mode === 'wide'
              ? { top: anchor.top, right: anchor.right, width: 300 }
              : { top: anchor.top, left: 16, right: 16, width: 'auto' }),
            // Safety net mirroring the bell's `.panel` max-width. Wide mode
            // is already < viewport at >=500px and narrow's left/right insets
            // bind first; this only guards pathological viewports so the
            // popover can never exceed the screen.
            maxWidth: 'calc(100vw - 24px)',
            background: 'var(--evi-surface)',
            border: '1px solid var(--evi-border)',
            borderRadius: 12,
            padding: 8,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
            // Matches NotificationBell's panel: above page content + the top
            // and tab bars (z 30 / 50), below toasts (z >= 1000).
            zIndex: 100,
          }}
        >
          <SectionLabel>Mode</SectionLabel>
          <ModeSegment value={mode} onChange={setMode} allowed={theme.availableModes} />

          <SectionLabel style={{ marginTop: 12 }}>Theme</SectionLabel>
          {inline.length === 0 ? (
            <div
              style={{
                padding: 10,
                fontSize: 12,
                color: 'var(--evi-text-mute)',
              }}
            >
              No themes available yet.
            </div>
          ) : (
            inline.map((entry) => (
              <ThemeRow
                key={entry.code}
                name={entry.name}
                description={entry.description}
                swatches={entry.swatches}
                active={entry.active}
                locked={entry.locked}
                onSelect={() => handleSelect(entry)}
              />
            ))
          )}

          <ViewAllRow onClick={handleOpenAll} />
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Footer row replacing the admin picker's "More themes coming soon"
 * text. Visually matches the shape of a ThemeRow so the transition
 * from the last short-list entry into "View all" feels continuous.
 */
function ViewAllRow({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--evi-surface-2)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 10,
        alignItems: 'center',
        width: '100%',
        padding: 10,
        marginTop: 4,
        borderTop: '1px solid var(--evi-border)',
        borderRadius: 8,
        border: 'none',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        cursor: 'pointer',
        background: 'transparent',
        color: 'var(--evi-text)',
        fontFamily: 'inherit',
        textAlign: 'left',
        fontSize: 13,
        fontWeight: 500,
        transition: 'background 0.15s ease',
      }}
    >
      <span>View all your themes</span>
      <span
        aria-hidden
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'var(--evi-text-mute)',
        }}
      >
        <ChevronRightIcon />
      </span>
    </button>
  );
}

// ── Helper for callers: compose a unified entry list ─────────────────────

/**
 * Convenience helper for consumers building the `entries` prop from
 * the registry + unlocks catalogue. Not strictly required — callers
 * can build entries any way they want — but this captures the
 * family-vs-skin mapping in one place.
 */
export interface BuildEntriesInput {
  activeFamilyId: ThemeFamilyId;
  activeSkinCode: string | null;
  unlocksCatalogue: Array<{
    code: string;
    name: string;
    description: string;
    category: string;
    metadata: Record<string, unknown>;
    owned: boolean;
    active: boolean;
  }>;
}

export function buildDriverPickerEntries(input: BuildEntriesInput): DriverPickerEntry[] {
  const out: DriverPickerEntry[] = [];

  // Admin families first — registry order (EVI Default, then Luce).
  // Display name runs through `driverThemeName` so driver surfaces can
  // re-brand (e.g. EVI Default → INITIATIVE) without touching the
  // shared registry.
  for (const t of listThemes()) {
    out.push({
      code: t.id,
      name: driverThemeName(t.id, t.name),
      description: t.description,
      swatches: t.swatches,
      // A family is "active" when it's the current theme AND no skin
      // is overriding it. Once a skin is applied the skin is the
      // active row, not the family it composes over.
      active: !input.activeSkinCode && t.id === input.activeFamilyId,
      locked: false,
      kind: 'family',
    });
  }

  // Skin unlocks next — seed insertion order from the catalogue is
  // preserved by the API so the short-list fallback is deterministic.
  for (const u of input.unlocksCatalogue) {
    if (u.category !== 'skin') continue;
    const meta = u.metadata as Record<string, unknown>;
    const variants = meta['variants'] as
      | { light?: Record<string, unknown>; dark?: Record<string, unknown> }
      | undefined;
    // Swatch sampling: prefer the dark variant's accent because most
    // skins started life as dark identities; fall through to light
    // and finally the legacy flat field for un-migrated docs.
    const accent =
      (variants?.dark?.['accentColor'] as string | undefined) ??
      (variants?.light?.['accentColor'] as string | undefined) ??
      (meta['accentColor'] as string | undefined) ??
      '#888';
    const charging =
      (variants?.dark?.['chargingColor'] as string | undefined) ??
      (variants?.light?.['chargingColor'] as string | undefined) ??
      (meta['chargingColor'] as string | undefined) ??
      accent;
    // Third swatch = a middle-ground mix between accent + charging so
    // the three-dot row reads as a palette snapshot rather than two
    // identical dots + a neutral.
    out.push({
      code: u.code,
      name: u.name,
      description: u.description,
      swatches: [accent, charging, '#111111'] as const,
      active: u.active,
      locked: !u.owned,
      kind: 'skin',
    });
  }

  return out;
}
